/* ============================================================================
 * collection-scroll-memory.js
 * ----------------------------------------------------------------------------
 * Merkt sich auf Collection-Seiten (z.B. "Alle Pflanzen"):
 *   - wie viele Seiten per "Mehr anzeigen" / Infinite Scroll nachgeladen wurden
 *   - die Scroll-Position
 *
 * Beim Zurueck-Navigieren werden die Produkte automatisch wieder nachgeladen
 * und exakt an die alte Stelle gescrollt. Waehrend das laeuft, liegt ein
 * Ladebildschirm ueber der Seite (siehe snippets/collection-scroll-memory-head.liquid),
 * damit der Kunde weder den Sprung nach oben noch das Nachladen sieht.
 *
 * Funktioniert mit allen drei Theme-Modi (pagination / show_more / infinite).
 * Keine Theme-Einstellung muss dafuer geaendert werden.
 * ========================================================================== */
(function () {
  'use strict';

  var STORAGE_KEY  = 'halo:collection-scroll';
  var MAX_AGE      = 30 * 60 * 1000; // 30 Minuten
  var MAX_PAGES    = 25;             // Sicherheitslimit gegen Endlosschleifen
  var STEP_TIMEOUT = 15000;          // max. Wartezeit pro Nachlade-Schritt (ms)
  var MAX_ENTRIES  = 12;             // wie viele Collections gemerkt werden
  var FADE_MS      = 300;            // muss zur CSS-Transition im Snippet passen

  var GRID_SELECTOR = '#CollectionProductGrid';
  var LIST_SELECTOR = '.productListing';
  var MORE_SELECTOR = '[data-infinite-scrolling]';

  var CLASS_ON   = 'collection-scroll-restoring';
  var CLASS_DONE = 'collection-scroll-restored';

  var restoring = false;
  var aborted   = false;

  /* ---------------------------------------------------------------- Helpers */

  function root()     { return document.documentElement; }
  function grid()     { return document.querySelector(GRID_SELECTOR); }
  function list()     { return document.querySelector(GRID_SELECTOR + ' ' + LIST_SELECTOR); }
  function moreLink() { return document.querySelector(GRID_SELECTOR + ' ' + MORE_SELECTOR); }

  function products() {
    var ul = list();
    return ul ? ul.querySelectorAll(':scope > .product') : [];
  }

  function isCollectionPage() {
    return !!grid() && !!list();
  }

  /* Key = Pfad + Filter-/Sortier-Parameter (ohne page) */
  function pageKey() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
      params.delete('page');
    } catch (e) {
      return window.location.pathname + window.location.search;
    }
    var qs = params.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  /* "page"-Parameter aus dem Mehr-anzeigen-Link lesen (= naechste Seite) */
  function nextPageOf(link) {
    if (!link) return null;
    var href = link.getAttribute('data-href') || link.getAttribute('href') || '';
    var match = href.match(/[?&]page=(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  function readStore() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function writeStore(store) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (e) { /* Storage voll / deaktiviert -> egal */ }
  }

  function pruneStore(store) {
    var now = Date.now();
    Object.keys(store).forEach(function (k) {
      if (!store[k] || now - (store[k].ts || 0) > MAX_AGE) delete store[k];
    });
    Object.keys(store).sort(function (a, b) {
      return (store[b].ts || 0) - (store[a].ts || 0);
    }).slice(MAX_ENTRIES).forEach(function (k) { delete store[k]; });
    return store;
  }

  /* ------------------------------------------------------------ Ladeoverlay */

  function overlayActive() {
    return root().classList.contains(CLASS_ON);
  }

  function hideOverlay() {
    var el = root();
    if (!el.classList.contains(CLASS_ON)) return;
    el.classList.add(CLASS_DONE);
    setTimeout(function () {
      el.classList.remove(CLASS_ON, CLASS_DONE);
    }, FADE_MS + 20);
  }

  /* --------------------------------------------------------------- Speichern */

  /* Anker = oberstes im Viewport sichtbares Produkt + Feinversatz.
     Robuster als eine reine Pixel-Position, falls Bilder anders umbrechen. */
  function currentAnchor() {
    var items = products();
    for (var i = 0; i < items.length; i++) {
      var rect = items[i].getBoundingClientRect();
      if (rect.bottom > 0) return { index: i, offset: Math.round(rect.top) };
    }
    return { index: -1, offset: 0 };
  }

  function saveState() {
    if (restoring || !isCollectionPage()) return;

    var link   = moreLink();
    var loaded = link ? (nextPageOf(link) || 1) - 1 : null; // null = alles geladen
    var anchor = currentAnchor();

    var store = pruneStore(readStore());
    store[pageKey()] = {
      y:      Math.round(window.pageYOffset),
      pages:  loaded,
      count:  products().length,
      anchor: anchor.index,
      offset: anchor.offset,
      ts:     Date.now()
    };
    writeStore(store);
  }

  /* -------------------------------------------------------- Wiederherstellen */

  function waitForMoreProducts(before) {
    return new Promise(function (resolve) {
      var started = Date.now();
      var timer = setInterval(function () {
        if (aborted)                    { clearInterval(timer); return resolve(false); }
        if (products().length > before) { clearInterval(timer); return resolve(true); }
        if (Date.now() - started > STEP_TIMEOUT) { clearInterval(timer); return resolve(false); }
      }, 100);
    });
  }

  function loadPagesUntil(targetPages) {
    var steps = 0;

    function step() {
      if (aborted || steps >= MAX_PAGES) return Promise.resolve();

      var link = moreLink();
      if (!link) return Promise.resolve();                 // alles geladen

      var next = nextPageOf(link);
      if (targetPages !== null && next !== null && next > targetPages) {
        return Promise.resolve();                          // Ziel erreicht
      }

      var before = products().length;
      steps++;
      link.click();                                        // nutzt die Theme-Logik

      return waitForMoreProducts(before).then(function (ok) {
        return ok ? step() : undefined;
      });
    }

    return step();
  }

  function targetFor(state) {
    var items = products();
    if (state.anchor >= 0 && items[state.anchor]) {
      var rect = items[state.anchor].getBoundingClientRect();
      return Math.max(0, Math.round(rect.top + window.pageYOffset - state.offset));
    }
    return Math.max(0, state.y || 0);
  }

  /* Springt an die Zielposition und justiert nach, solange das Overlay noch
     liegt - dadurch sieht der Kunde von den Korrekturen nichts. */
  function settleAt(state) {
    return new Promise(function (resolve) {
      var tries = 0;

      function tick() {
        var target = targetFor(state);
        if (Math.abs(window.pageYOffset - target) > 2) window.scrollTo(0, target);

        tries++;
        if (tries >= 3 && Math.abs(window.pageYOffset - targetFor(state)) <= 2) return resolve();
        if (tries >= 6) return resolve();
        setTimeout(tick, 90);
      }

      tick();
    });
  }

  function restore(state) {
    restoring = true;
    aborted   = false;

    // Escape bricht ab, falls doch mal etwas haengt
    var onKey = function (e) { if (e.key === 'Escape') aborted = true; };
    document.addEventListener('keydown', onKey);

    var needsMore = state.pages === null || (state.pages && state.pages > 1);

    Promise.resolve()
      .then(function () { return needsMore ? loadPagesUntil(state.pages) : null; })
      .then(function () { return settleAt(state); })
      .catch(function () { /* trotzdem sauber aufraeumen */ })
      .then(function () {
        document.removeEventListener('keydown', onKey);
        restoring = false;
        hideOverlay();
      });
  }

  /* ------------------------------------------------------------------- Init */

  function init() {
    if (!isCollectionPage()) { hideOverlay(); return; }

    // Browser soll nicht selbst an eine falsche Stelle springen
    try { history.scrollRestoration = 'manual'; } catch (e) { /* noop */ }

    var throttle = null;
    window.addEventListener('scroll', function () {
      if (throttle) return;
      throttle = setTimeout(function () { throttle = null; saveState(); }, 250);
    }, { passive: true });

    window.addEventListener('pagehide', saveState);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') saveState();
    });

    // Klick auf ein Produkt: Zustand sofort sichern
    var g = grid();
    if (g) g.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('a[href]')) saveState();
    }, true);

    // Das Head-Snippet hat bereits entschieden, ob wiederhergestellt wird.
    if (!overlayActive()) return;

    var state = readStore()[pageKey()];
    if (!state || Date.now() - (state.ts || 0) > MAX_AGE) { hideOverlay(); return; }

    restore(state);
  }

  function boot(event) {
    // bfcache: Browser hat DOM + Scroll bereits vollstaendig -> nichts tun
    if (event && event.persisted) { hideOverlay(); return; }
    init();
  }

  window.addEventListener('pageshow', boot);
})();
